import { Box, ColorOptions, Icon, Loader, Spacer, TextNew } from '@jfs/ui-new'
import { FC, useCallback, useEffect, useRef, useState } from 'react'
import { FlatList } from 'react-native'
import { TableComponent } from '../TableComponent'
import MonthView from './MonthView'
import WeekDayView from './WeekdayView'
import styles from './styles'
import { CalendarTypes, CalendarViewProps, Day, MonthProps } from './types'

const testIdLeftTab = 'left-test'
const testIdRightTab = 'right-test'

let userSelection: Day
let mCurrentUpdate: boolean
const extraDatas: Map<string, boolean> = new Map()
let shouldScrollUp: boolean
export const isLoadNext = (
  maxYear: number,
  maxMonth: number,
  cYear: number,
  startYear: number,
  mCount: number,
): boolean => {
  if (maxYear !== -1 && (cYear > maxYear || startYear > maxYear)) {
    return false
  }

  if (maxMonth !== -1 && mCount >= maxMonth) {
    return false
  }
  return true
}

const getDayKey = (day: Day): string => `${day.m}-${day.y}`
export const getDateKey = (date: Date): string => {
  return `${date.getMonth()}-${date.getFullYear()}`
}

const dateSplitReducer = (acc: Map<string, Array<number>>, d: string): Map<string, Array<number>> => {
  const ddate = new Date(d)
  const p = getDateKey(ddate)
  let array = acc.get(p)
  if (array) {
    array.push(ddate.getDate())
  } else {
    array = [ddate.getDate()]
  }
  acc.set(p, array)
  return acc
}

export const monthDiff = (d1: Date, d2: Date): number => {
  let months
  months = (d2.getFullYear() - d1.getFullYear()) * 12
  months -= d1.getMonth()
  months += d2.getMonth() + 1
  return months <= 0 ? 0 : months
}

const CalendarView: FC<CalendarViewProps> = ({
  ID,
  holidayList = [],
  preSelectedDate,
  onDateSelected,
  refreshFlag,
  minDate,
  maxDate,
  type,
  secondSelectedDate,
  maxDuration,
  scrollToCurrentDate,
}: CalendarViewProps) => {
  const startYear = minDate.getFullYear()
  const startMonth = minDate.getMonth()
  let maxMonth = maxDate.getMonth()
  const maxYear = maxDate.getFullYear()
  const disableBefore = minDate
  const disableAfter = maxDate
  maxMonth = monthDiff(minDate, maxDate)

  const [cRefresh, setRefresh] = useState<boolean>(refreshFlag)
  const shouldRefresh = cRefresh !== refreshFlag

  if (shouldRefresh) {
    shouldScrollUp = true
    setRefresh(refreshFlag)
  }
  const [cMonth, setMonth] = useState(startMonth)
  const [cYear, setYear] = useState(startYear)
  const [update, processUpdate] = useState<boolean>(false)
  mCurrentUpdate = update
  const [mappedDate, setMappedDate] = useState<Map<string, Array<number>>>(new Map())
  const [mCount, setTotalMonthRendered] = useState<number>(0)
  const [monthsProps, setMonthsProps] = useState<Array<MonthProps>>([])
  const [monthsRange, setMonthsRange] = useState<Array<number>>([])
  const [firstDate, setFirstDate] = useState(null)
  const [hoveringDay, setHoveringDay] = useState(null)
  const onEndReachedThreshold = 3

  const loadData = (
    pageSize: number,
    month: number,
    year: number,
    count: number,
    mMappedDate: Map<string, Array<number>>,
    shouldRefresh: boolean,
  ): void => {
    let tempMonth = month
    let tempYear = year
    let tempCount = count
    const mMonths: Array<MonthProps> = []
    for (let i = 0; i < pageSize && isLoadNext(maxYear, maxMonth, tempYear, startYear, tempCount); i += 1) {
      let cDisableBefore = 0
      const lastDay = new Date(tempYear, tempMonth + 1, 0)
      const firstDay = new Date(tempYear, tempMonth, 1)
      let cDisableAfter = -1
      let hoverFrom = null
      let hoverTo = null
      let cPreSelectedDate: number | undefined
      if (disableBefore !== undefined && disableBefore > firstDay) {
        if (
          disableBefore?.getFullYear() === lastDay?.getFullYear() &&
          disableBefore?.getMonth() === lastDay?.getMonth() &&
          disableBefore?.getDate() === lastDay?.getDate()
        ) {
          cDisableBefore = disableBefore.getDate()
        } else if (disableBefore > lastDay) {
          cDisableBefore = lastDay.getDate() + 1
        } else {
          cDisableBefore = disableBefore.getDate()
        }
      }
      if (disableAfter !== undefined && disableAfter < lastDay) {
        if (disableAfter < firstDay) {
          cDisableAfter = 0
        } else {
          cDisableAfter = disableAfter.getDate()
        }
      }

      if (
        preSelectedDate !== undefined &&
        preSelectedDate.getFullYear() === tempYear &&
        preSelectedDate.getMonth() === tempMonth
      ) {
        cPreSelectedDate = preSelectedDate.getDate()
      } else {
        cPreSelectedDate = undefined
      }

      const hDate = new Date(`${hoveringDay?.m + 1}/${hoveringDay?.d}/${hoveringDay?.y}`)?.getTime()

      if (secondSelectedDate) {
        if (hDate > 0) {
          if (secondSelectedDate?.getTime() < hDate) {
            hoverFrom = secondSelectedDate?.getTime()
            hoverTo = hDate
          } else {
            hoverTo = secondSelectedDate?.getTime()
            hoverFrom = hDate
          }
        } else {
          if (preSelectedDate?.getTime() < secondSelectedDate?.getTime()) {
            hoverFrom = preSelectedDate?.getTime()
            hoverTo = secondSelectedDate?.getTime()
          } else {
            hoverTo = preSelectedDate?.getTime()
            hoverFrom = secondSelectedDate?.getTime()
          }
        }
      } else {
        if (preSelectedDate?.getTime() < hDate) {
          hoverFrom = preSelectedDate?.getTime()
          hoverTo = hDate
        } else {
          hoverTo = preSelectedDate?.getTime()
          hoverFrom = hDate
        }
      }
      mMonths.push({
        type,
        month: tempMonth,
        year: tempYear,
        holidayList: mMappedDate.get(`${tempMonth}-${tempYear}`),
        disableBefore: cDisableBefore,
        disableAfter: cDisableAfter,
        preSelectedDate: cPreSelectedDate,
        selectedDate: preSelectedDate,
        hoverFrom: hoverFrom,
        hoverTo: hoverTo,
      })
      if (!extraDatas.has(`${tempMonth}-${tempYear}`)) {
        extraDatas.set(`${tempMonth}-${tempYear}`, false)
      }
      if (tempMonth === 11) {
        tempMonth = 0
        tempYear += 1
      } else {
        tempMonth += 1
      }
      tempCount += 1
    }
    let monthprops = monthsProps.concat(mMonths)
    if (type === CalendarTypes.HISTORY || type === CalendarTypes.SINGLE) {
      monthprops = monthprops.reverse()
    }
    if (!isLoadNext(maxYear, maxMonth, tempMonth, startYear, tempCount) && maxMonth !== -1) {
      monthprops = monthprops.slice(0, maxMonth)
    }

    setMonthsProps(type === CalendarTypes.SINGLE_FUTURE ? monthprops : monthprops.reverse())
    !shouldRefresh &&
      setMonthsRange(type === CalendarTypes.SINGLE_FUTURE ? [0, 2] : [monthprops.length - 2, monthprops.length])
    !shouldRefresh &&
      scrollToCurrentDate &&
      !maxDuration &&
      setMonthsRange(type === CalendarTypes.SINGLE_FUTURE ? [0, 2] : [monthprops.length - 3, monthprops.length - 1])
    setTotalMonthRendered(tempCount)
    setMonth(tempMonth)
    setYear(tempYear)
  }

  useEffect(() => {
    let cMappedData = new Map()
    loadData(maxMonth, minDate.getMonth(), minDate.getFullYear(), 0, cMappedData, shouldRefresh)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [minDate, maxDate])
  const cFlatList = useRef<FlatList<MonthProps>>(null)

  const onDateSelection = (day: Day): void => {
    const date: Date = new Date(Date.now())
    if (onDateSelected) {
      onDateSelected(new Date(day.y, day.m, day.d))

      if (firstDate) {
        day.isPreSelected = maxDuration ? false : true
        return
      }
      setFirstDate(new Date(day.y, day.m, day.d, date.getHours(), date.getMinutes(), date.getSeconds()))
    }
    if (type !== CalendarTypes.SINGLE || CalendarTypes.SINGLE_FUTURE) {
      return
    }
    if (userSelection) {
      userSelection.isSelected = false
      if (day.m !== userSelection.m || day.y !== userSelection.y) {
        extraDatas.set(getDayKey(userSelection), !extraDatas.get(getDayKey(userSelection)))
      }
    }
    extraDatas.set(getDayKey(day), !extraDatas.get(getDayKey(day)))

    userSelection = day
    userSelection.isSelected = true
    processUpdate(!mCurrentUpdate)
  }

  const onEndReached = (): void => {
    if (monthsProps.length > 0 && isLoadNext(maxYear, maxMonth, cYear, startYear, mCount)) {
      loadData(3, cMonth, cYear, mCount, mappedDate, false)
    }
  }
  const keyExtractor = useCallback((item) => `${item.month}-${item.year}`, [])
  const mNames: Array<string> = [
    'January',
    'February',
    'March',
    'April',
    'May',
    'June',
    'July',
    'August',
    'September',
    'October',
    'November',
    'December',
  ]
  const renderItem = ({
    item: {
      type: mType,
      month,
      year,
      holidayList: mHolidayList,
      disableBefore: mDisableBefore,
      disableAfter: mDisableAfter,
      preSelectedDate: mPreSelectedDate,
      hoverFrom: mHoverFrom,
      hoverTo: mHoverTo,
    },
  }: {
    item: MonthProps
  }): JSX.Element => (
    <Box {...styles[type].calendarItemStyle} testID={'calandar-month-view-' + mNames[month]}>
      <TextNew {...styles[type].MonthTitleStyle} testID="calendr-month">{`${mNames[month]} ${year}`}</TextNew>
      <Spacer size={16} />
      <Box h={1} width="100%" backgroundColor={'GREY_SEPARATOR'} />
      <WeekDayView ID={`${ID}.WeekDayView`} type={type} />
      <Spacer size={20} />
      <MonthView
        type={mType}
        month={month}
        year={year}
        holidayList={mHolidayList}
        disableBefore={mDisableBefore}
        disableAfter={mDisableAfter}
        preSelectedDate={mPreSelectedDate}
        extraData={extraDatas.get(`${month}-${year}`)}
        onDateSelected={onDateSelection}
        selectedDate={preSelectedDate}
        secondSelectedDate={secondSelectedDate?.getMonth() === month ? secondSelectedDate?.getDate() : -1}
        onHover={(day) => {
          setHoveringDay(day)
          setRefresh(true)
        }}
        hoverFrom={mHoverFrom}
        hoverTo={mHoverTo}
        maxDuration={maxDuration}
      />
      <Spacer size={16} />
      <Box h={1} width="100%" backgroundColor={'GREY_SEPARATOR'} />
    </Box>
  )

  if (monthsProps.length === 0 || shouldRefresh) {
    monthsProps.length = 0
    if (userSelection) {
      userSelection.isSelected = false
    }
    setMonth(minDate.getMonth())
    setYear(minDate.getFullYear())
    setTotalMonthRendered(0)

    extraDatas.forEach((value, key) => {
      extraDatas.set(key, !value)
    })
    let cMappedData = new Map()
    if (holidayList.length > 0) {
      cMappedData = holidayList.reduce(dateSplitReducer, new Map<string, Array<number>>())

      setMappedDate(cMappedData)
    }
    loadData(maxMonth, minDate.getMonth(), minDate.getFullYear(), 0, cMappedData, shouldRefresh)
  }
  if (cFlatList && cFlatList.current && shouldScrollUp) {
    shouldScrollUp = false
    const b: FlatList<MonthProps> = cFlatList?.current
    if (b) {
      b.scrollToOffset({ animated: false, offset: 0 })
    }
  }

  const handleLeftArrow = () => {
    setMonthsRange([monthsRange[0] - 1, monthsRange[1] - 1])
  }
  const leftArrowDisabled = monthsRange[0] === 0
  const leftArrowColor = leftArrowDisabled ? ('DISABLED_GREY' as ColorOptions) : ('BLACK' as ColorOptions)

  const handleRightArrow = () => setMonthsRange([monthsRange[0] + 1, monthsRange[1] + 1])
  const rightArrowDisabled = monthsRange[1] === monthsProps.length || monthsProps.length === 1
  const rightArrowColor = rightArrowDisabled ? ('DISABLED_GREY' as ColorOptions) : ('BLACK' as ColorOptions)
  const monthsList = monthsProps.slice(monthsRange[0], monthsRange[1]) ?? []
  return (
    <Box accessibilityLabel={`${ID}.Container`} testID={`${ID}.Container`}>
      <TableComponent
        value={''} //Routes value
        canFetchMore={false}
        hide={monthsList?.length === 0}
        isCustom={true}
        ListContent={monthsList}
        customStyle={styles[type].CalendarListStyle}
        testID={`${ID}.CalendarList`}
        innerRef={cFlatList}
        extraData={update}
        keyExtractor={keyExtractor}
        RenderCustomElement={renderItem}
        overScrollMode="never"
        horizontal
        scrollEnabled={false}
        accessibilityLabel={`${ID}.CalendarList`}
        onEndReached={onEndReached}
        onEndReachedThreshold={onEndReachedThreshold}
      />
      {monthsList?.length === 0 && (
        <Box h={250} w={696} justify={'center'} align={'center'}>
          <Loader />
        </Box>
      )}
      <Box {...styles[type].arrowsView}>
        <Box
          testID={testIdLeftTab}
          underlayColor="TRANSPARENT"
          disabled={monthsRange[0] === 0}
          onPress={handleLeftArrow}
          {...styles[type].arrowContainer}>
          <Icon id={'ARROW_LEFT'} size={16} color={leftArrowColor} />
        </Box>
        <Box
          testID={testIdRightTab}
          underlayColor="TRANSPARENT"
          {...styles[type].arrowContainer}
          disabled={rightArrowDisabled}
          onPress={handleRightArrow}>
          <Icon id={'ARROW_RIGHT'} size={16} color={rightArrowColor} />
        </Box>
      </Box>
    </Box>
  )
}

export default CalendarView
